import type {
  StoreCategoryPublicDescription,
  StoreCategoryPublicDescriptionLocale,
} from '../types/category';

type UnknownRecord = Record<string, unknown>;

export const STORE_CATEGORY_PUBLIC_DESCRIPTION_MAX_LENGTH = 240;
export const STORE_CATEGORY_PUBLIC_DESCRIPTION_LOCALES = [
  'ar',
  'he',
  'en',
] as const satisfies readonly StoreCategoryPublicDescriptionLocale[];

export type StoreCategoryPublicDescriptionValidationIssue =
  | 'invalid_shape'
  | 'too_long'
  | 'unsafe_text';

export type StoreCategoryPublicDescriptionValidationResult =
  | {
      readonly ok: true;
      readonly value: StoreCategoryPublicDescription | null;
    }
  | {
      readonly ok: false;
      readonly issue: StoreCategoryPublicDescriptionValidationIssue;
      readonly value: null;
    };

const UNSAFE_PUBLIC_DESCRIPTION_RE =
  /<|>|<\/|https?:\/\/|www\.|javascript:|data:|vbscript:|on[a-z]+\s*=|style\s*=|[\u007f-\u009f\u202a-\u202e\u2066-\u2069]/iu;
const MARKDOWN_LIKE_RE = /`|!\[|\[[^\]]*\]\([^)]*\)|(?:^|\s)#{1,6}\s|\*\*|__/u;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function hasControlCharacters(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || codePoint === 0x7f) return true;
  }
  return false;
}

function normalizePublicDescriptionText(
  value: unknown,
):
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false; readonly issue: StoreCategoryPublicDescriptionValidationIssue } {
  if (typeof value !== 'string') return { ok: false, issue: 'invalid_shape' };
  const normalized = value.trim().replace(/\s+/gu, ' ');
  if (normalized.length > STORE_CATEGORY_PUBLIC_DESCRIPTION_MAX_LENGTH) {
    return { ok: false, issue: 'too_long' };
  }
  if (
    normalized.length > 0 &&
    (UNSAFE_PUBLIC_DESCRIPTION_RE.test(normalized) ||
      MARKDOWN_LIKE_RE.test(normalized) ||
      hasControlCharacters(normalized))
  ) {
    return { ok: false, issue: 'unsafe_text' };
  }
  return { ok: true, value: normalized };
}

export function validateStoreCategoryPublicDescription(
  value: unknown,
): StoreCategoryPublicDescriptionValidationResult {
  if (value === null || value === undefined) return { ok: true, value: null };
  if (!isRecord(value) || !hasExactKeys(value, STORE_CATEGORY_PUBLIC_DESCRIPTION_LOCALES)) {
    return { ok: false, issue: 'invalid_shape', value: null };
  }

  const normalized: Record<StoreCategoryPublicDescriptionLocale, string> = {
    ar: '',
    he: '',
    en: '',
  };

  for (const locale of STORE_CATEGORY_PUBLIC_DESCRIPTION_LOCALES) {
    const result = normalizePublicDescriptionText(value[locale]);
    if (!result.ok) return { ok: false, issue: result.issue, value: null };
    normalized[locale] = result.value;
  }

  if (normalized.ar === '' && normalized.he === '' && normalized.en === '') {
    return { ok: true, value: null };
  }

  return {
    ok: true,
    value: {
      ar: normalized.ar,
      he: normalized.he,
      en: normalized.en,
    },
  };
}

export function sanitizeStoreCategoryPublicDescription(
  value: unknown,
): StoreCategoryPublicDescription | null {
  const result = validateStoreCategoryPublicDescription(value);
  return result.ok ? result.value : null;
}

export function isStoreCategoryPublicDescription(
  value: unknown,
): value is StoreCategoryPublicDescription {
  return validateStoreCategoryPublicDescription(value).value !== null;
}

export function resolveStoreCategoryPublicDescription(
  value: StoreCategoryPublicDescription | null | undefined,
  locale: string | null | undefined,
  fallbackLocale: StoreCategoryPublicDescriptionLocale = 'ar',
): string {
  if (!value) return '';
  const baseLocale = locale?.split('-')[0] as StoreCategoryPublicDescriptionLocale | undefined;
  const activeLocale = STORE_CATEGORY_PUBLIC_DESCRIPTION_LOCALES.includes(
    baseLocale as StoreCategoryPublicDescriptionLocale,
  )
    ? (baseLocale as StoreCategoryPublicDescriptionLocale)
    : 'ar';
  const order: readonly StoreCategoryPublicDescriptionLocale[] = [
    activeLocale,
    fallbackLocale,
    'ar',
    'he',
    'en',
  ];
  const seen = new Set<StoreCategoryPublicDescriptionLocale>();
  for (const candidateLocale of order) {
    if (seen.has(candidateLocale)) continue;
    seen.add(candidateLocale);
    const candidate = value[candidateLocale]?.trim();
    if (candidate) return candidate;
  }
  return '';
}
